import { listMembersAssignedQueryOptions } from '@/api/listQueryOptions'
import LoadProgress from '@/components/LoadProgress'
import { Box } from '@mui/material'
import { DataGridPremium, GridColDef, GridInitialState, GridPivotModel, GridSidebarValue } from '@mui/x-data-grid-premium'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { t } from 'i18next'

type ListMemberPivotSearch = {
    listHeaderUuids: string[]
}

export const Route = createFileRoute('/_auth/listMemberPivot')({
    component: ListMemberPivotComponent,
    validateSearch: (search: Record<string, unknown>): ListMemberPivotSearch => {
        // validate and parse the search params into a typed state
        return {
            listHeaderUuids: (search.listHeaderUuids as string[]) || [],
        }
    },
})

function ListMemberPivotComponent() {
    const { listHeaderUuids } = Route.useSearch()

    const listMembersAssignedQuery = useSuspenseQuery(listMembersAssignedQueryOptions(listHeaderUuids))
    const listMembersAssigned = listMembersAssignedQuery.data || []
    const listMembersEnhanced = listMembersAssigned.map((lm) => {
        return {
            ...lm,
            assigned: 1,
            uuid: lm.uuid || lm.member?.uuid || '',
        }
    })

    const columns: GridColDef[] = []
    columns.push(
        {
            field: 'scoutId',
            minWidth: 50,
            headerName: t('property.member.scoutId'),
            valueGetter: (_value, row) =>
                `${row.member?.scoutId}`,
        })
    columns.push(
        {
            field: 'member',
            minWidth: 100,
            headerName: t('property.general.name'),

            valueGetter: (_value, row) =>
                `${row.member?.lastname} ${row.member?.firstname}`,
        })

    columns.push(
        {
            field: 'listHeader',
            minWidth: 50,
            headerName: t('entity.listHeader'),

            valueGetter: (_value, row) =>
                `${row.listHeader.code} - ${row.listHeader.startDate && dayjs(row.listHeader.startDate).format('DD.MM.YYYY')}`,
        })
    columns.push(
        {
            field: 'assigned',
            minWidth: 50,
            headerName: t('property.listMember.assigned'),
        })

    const pivotModel: GridPivotModel = {
        rows: [{ field: 'member' }],
        columns: [{ field: 'listHeader' }],
        values: [
            { field: 'assigned', aggFunc: 'sum' },
        ],
    };

    const initialState: GridInitialState = {
        pivoting: {
            enabled: true,
            model: pivotModel,
        },
        sorting: {
            sortModel: [
                { field: 'member', sort: 'asc' },
            ],
        },
        sidebar: {
            open: true,
            value: GridSidebarValue.Pivot,
        },
    };

    if (listMembersAssignedQuery.isLoading || listMembersAssignedQuery.isFetching || !listMembersAssigned) {
        return <LoadProgress />
    }

    return (
        <Box sx={{
            height: { xs: `calc(100% - 50px)` }
        }}>
            <DataGridPremium
                rows={listMembersEnhanced}
                getRowId={(row) => {
                    return row.uuid || ''
                }}
                columns={columns}
                initialState={initialState}
                showToolbar
                slotProps={{
                    toolbar: {
                        showQuickFilter: false,
                        csvOptions: {
                            disableToolbarButton: true
                        },
                        printOptions: {
                            disableToolbarButton: true
                        }
                    },
                }}
                density='compact'
                hideFooter={true}
                disableDensitySelector
                disableAggregation
                disableColumnPinning
                disableColumnReorder
                disableRowGrouping
                disableRowSelectionOnClick
            />
        </Box>
    );
}
